import React, { RefAttributes } from "react";
import moment from "moment-timezone";
import { SectionType, FormInputType, SectionProps } from "@wellth/web-ui";
import {
  titleForGender,
  titleForLanguageCode,
  titleForTimezone,
} from "constants/strings";
import { SectionHeader } from "components/SectionHeader";
import { ProfileSectionProps as BaseProps } from "../types";

export interface ProfileSectionProps extends BaseProps, RefAttributes<any> {
  onEditButtonPressed: () => void;
  inputStyle?: React.CSSProperties;
}

export const Inputs = ({
  birthdate,
  language,
  gender,
  race,
  ethnicity,
  timezone,
  externalId,
  prospectId,
  siteName,
  eligibilityEndDate,
  medicareId,
  medicaidId,
  programTemplate,
  coverageType,
  planName,
  pcpName,
  pcpNumber,
  onEditButtonPressed,
  inputStyle = { height: "2.1875rem", width: "18.75rem" },
  ref,
}: ProfileSectionProps): SectionProps<any> => ({
  ref,
  title: (
    <SectionHeader
      title="Profile"
      submitButtonType="edit"
      onClick={onEditButtonPressed}
    />
  ),
  key: "profile",
  type: SectionType.Form,
  data: {
    mapPropsToFields: (props, createFormField) => ({
      birthdate: createFormField({
        value: birthdate ? moment(birthdate).format("L") : "",
      }),
      language: createFormField({
        value: titleForLanguageCode(language),
      }),
      gender: createFormField({
        value: titleForGender(gender),
      }),
      race: createFormField({
        value: race,
      }),
      ethnicity: createFormField({
        value: ethnicity,
      }),
      timezone: createFormField({
        value: titleForTimezone(timezone),
      }),
      localTime: createFormField({
        value: timezone ? moment.tz(timezone).format("h:mm A (z)") : "",
      }),
      externalId: createFormField({
        value: externalId,
      }),
      prospectId: createFormField({
        value: prospectId,
      }),
      siteName: createFormField({
        value: siteName,
      }),
      eligibilityEndDate: createFormField({
        value: eligibilityEndDate ? moment(eligibilityEndDate).format("L") : "",
      }),
      medicareId: createFormField({
        value: medicareId,
      }),
      medicaidId: createFormField({
        value: medicaidId,
      }),
      programTemplate: createFormField({
        value: programTemplate,
      }),
      coverageType: createFormField({
        value: coverageType,
      }),
      planName: createFormField({
        value: planName,
      }),
      pcpName: createFormField({
        value: pcpName,
      }),
      pcpNumber: createFormField({
        value: pcpNumber,
      }),
    }),
    inputs: [
      {
        type: FormInputType.Info,
        title: "Birthday",
        key: "birthdate",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Language",
        key: "language",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Gender",
        key: "gender",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Race",
        key: "race",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Ethnicity",
        key: "ethnicity",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        key: "timezone",
        title: "Latest Timezone:",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Local Time",
        key: "localTime",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Customer Provided ID",
        key: "externalId",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Prospect ID",
        key: "prospectId",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Site Name",
        key: "siteName",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Eligibility End Date",
        key: "eligibilityEndDate",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Medicare ID",
        key: "medicareId",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Medicaid ID",
        key: "medicaidId",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Program Template",
        key: "programTemplate",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Coverage Type",
        key: "coverageType",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Plan Name",
        key: "planName",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Primary Care Provider",
        key: "pcpName",
        style: inputStyle,
      },
      {
        type: FormInputType.Info,
        title: "Primary Care Provider Contact",
        key: "pcpNumber",
        style: inputStyle,
      },
    ],
    labelCol: { sm: { span: 8 }, xs: { span: 8 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 12 } },
  },
});

export default Inputs;
